/**
 * Access management: the Access page (a tenant's own administrators) and the
 * Tenants page (platform administrators). The BFF authorizes every call from
 * Mongo and deployment config; the SPA only hides what the server would refuse.
 */
import type {
  AccessAuditEntry,
  AccessMember,
  AccessTenant,
  CreateTenantRequest,
  InviteRequest,
  InviteResult,
  TenantFlowsView,
  TenantReadiness,
  UpdateMemberRequest,
  UpdateTenantFlowsRequest,
  UpdateTenantRequest
} from '@portal/contracts'
import { apiFetch } from './portal-client'

const t = (tenantId: string) => `/api/access/tenants/${encodeURIComponent(tenantId)}`
const m = (tenantId: string, userId: string) => `${t(tenantId)}/members/${encodeURIComponent(userId)}`

export const getAccessTenant = (tenantId: string) => apiFetch<AccessTenant>(t(tenantId))
export const listMembers = (tenantId: string) => apiFetch<AccessMember[]>(`${t(tenantId)}/members`)
export const inviteMembers = (tenantId: string, body: InviteRequest) =>
  apiFetch<InviteResult[]>(`${t(tenantId)}/members`, { method: 'POST', body: JSON.stringify(body) })
export const updateMember = (tenantId: string, userId: string, body: UpdateMemberRequest) =>
  apiFetch<AccessMember>(m(tenantId, userId), { method: 'PATCH', body: JSON.stringify(body) })
export const resendInvite = (tenantId: string, userId: string, locale: 'en' | 'pt-BR') =>
  apiFetch<InviteResult>(`${m(tenantId, userId)}/resend-invite`, { method: 'POST', body: JSON.stringify({ locale }) })
export const signOutMember = (tenantId: string, userId: string) =>
  apiFetch<void>(`${m(tenantId, userId)}/sign-out`, { method: 'POST', body: JSON.stringify({}) })
export const resetMemberPasskeys = (tenantId: string, userId: string) =>
  apiFetch<{ removed: number }>(`${m(tenantId, userId)}/reset-passkeys`, { method: 'POST', body: JSON.stringify({}) })
export const getAccessAudit = (tenantId: string) => apiFetch<AccessAuditEntry[]>(`${t(tenantId)}/audit?limit=50`)

export const listTenants = () => apiFetch<AccessTenant[]>('/api/platform/tenants')
export const createTenant = (body: CreateTenantRequest) =>
  apiFetch<{ tenant: AccessTenant; invite?: InviteResult }>('/api/platform/tenants', {
    method: 'POST',
    body: JSON.stringify(body)
  })
export const updateTenant = (tenantId: string, body: UpdateTenantRequest) =>
  apiFetch<AccessTenant>(`/api/platform/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  })
export const getTenantReadiness = (tenantId: string) =>
  apiFetch<TenantReadiness>(`/api/platform/tenants/${encodeURIComponent(tenantId)}/readiness`)
export const getTenantFlows = (tenantId: string) =>
  apiFetch<TenantFlowsView>(`/api/platform/tenants/${encodeURIComponent(tenantId)}/flows`)
export const updateTenantFlows = (tenantId: string, body: UpdateTenantFlowsRequest) =>
  apiFetch<TenantFlowsView>(`/api/platform/tenants/${encodeURIComponent(tenantId)}/flows`, {
    method: 'PUT',
    body: JSON.stringify(body)
  })
